import { PermissionSetMap } from "constants/RBACConstants";
import useModalRenderer from "hooks/useModalRenderer";
import { filter, forEach, isFunction, noop } from "lodash";
import React, {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import AlertsAPI from "restAPI/AlertsAPI";
import { hasPermission } from "utils/authService";
import AnyaModal from "views/Dashboards/AnyaModal";

import { useGlobalFilterContext } from "./GlobalFilterContext";

export const REFRESH_HIERARCHY_NODE_PRODUCT_SUBPRODUCT_DATA: unique symbol =
  Symbol("REFRESH_HIERARCHY_NODE_PRODUCT_SUBPRODUCT_DATA");

type EventType = typeof REFRESH_HIERARCHY_NODE_PRODUCT_SUBPRODUCT_DATA;

type ListenerFn = (payload: unknown) => void;

type ListenerType = <T extends EventType>(type: T, cb: ListenerFn) => void;

type TriggerEventType = <T extends EventType>(
  type: T,
  payload?: unknown,
) => void;

type Listeners = { [key in EventType]?: ListenerFn[] };

type AlertCountType =
  | {
      CRITICAL: number;
      // [key: string]: number;
    }
  | undefined;

type UtilitiesContextType = {
  fetchAlertCounts: () => void;
  alertsCount: AlertCountType;
  showAnyaSummaryPopup: (props?: any) => void;
  addEventListener: ListenerType;
  removeEventListener: ListenerType;
  triggerEvent: TriggerEventType;
};

const defaultValues: UtilitiesContextType = {
  fetchAlertCounts: noop,
  alertsCount: { CRITICAL: 0 },
  showAnyaSummaryPopup: noop,
  addEventListener: noop,
  removeEventListener: noop,
  triggerEvent: noop,
};

type UtilitiesContextProviderProps = {
  children: ReactNode;
};

const UtilitiesContext = React.createContext(defaultValues);

export default function UtilitiesProvider({
  children,
}: UtilitiesContextProviderProps) {
  const [alertsCount, setAlertsCount] = useState<AlertCountType>();
  const { globalFilterHash } = useGlobalFilterContext();
  const hasAlertPermission = hasPermission(PermissionSetMap.READ_ALERT);
  const [isAnyaOpen, setAnyaOpen] = useState<boolean>(false);
  const [events, setEvents] = useState<Listeners>({});

  const fetchAlertCounts = useCallback(() => {
    if (!hasAlertPermission) {
      return;
    }
    const { promise, cancel } = AlertsAPI.getUnreadAlertCount({
      criticallity: ["CRITICAL"],
      createdAt: ["Last 30 days"],
    });
    promise.then((response) => {
      const { notification_stats } = response;
      setAlertsCount(notification_stats as AlertCountType);
    });

    return cancel;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalFilterHash, hasAlertPermission]);

  useEffect(() => fetchAlertCounts(), [fetchAlertCounts]);

  useEffect(() => {
    let cancel = fetchAlertCounts();
    const intervalId = setInterval(
      () => {
        cancel = fetchAlertCounts();
      },
      2 * 60 * 1000,
    ); // 2mins

    return () => {
      clearInterval(intervalId);
      if (isFunction(cancel)) {
        cancel();
      }
    };
  }, [fetchAlertCounts]);

  const modalRenderer = useModalRenderer();
  const showAnyaSummaryPopup = useCallback(
    (props?: any) => {
      if (isAnyaOpen === false) {
        setAnyaOpen(true);
        modalRenderer.show({
          //@ts-expect-error
          content: <AnyaModal props={props} />,
          defaultPlacement: "right",
          width: 799,
          mask: true,
          fullHeight: true,
          onClose: (val: any) => {
            setAnyaOpen(false);
          },
        });
      } else return;
    },
    [isAnyaOpen, modalRenderer],
  );

  const addEventListener: ListenerType = useCallback((type, cb) => {
    setEvents((events) => ({
      ...events,
      [type]: [...(events[type] || []), cb],
    }));
  }, []);

  const removeEventListener: ListenerType = useCallback((type, cb) => {
    setEvents((events) => ({
      ...events,
      [type]: filter(events[type], (callback) => callback !== cb),
    }));
  }, []);

  const triggerEvent: TriggerEventType = useCallback(
    (type, payload) => {
      if (events[type]) {
        forEach(events[type] ?? [], (entry) => entry(payload));
      }
    },
    [events],
  );

  const values = useMemo<UtilitiesContextType>(
    () => ({
      alertsCount,
      fetchAlertCounts,
      showAnyaSummaryPopup,
      addEventListener,
      removeEventListener,
      triggerEvent,
    }),
    [
      alertsCount,
      fetchAlertCounts,
      showAnyaSummaryPopup,
      addEventListener,
      removeEventListener,
      triggerEvent,
    ],
  );
  return (
    <UtilitiesContext.Provider value={values}>
      {children}
      {modalRenderer.context}
    </UtilitiesContext.Provider>
  );
}
export function useUtilities(): UtilitiesContextType {
  return useContext(UtilitiesContext);
}

export function withUtilities(Component: React.ComponentType<any>) {
  return function UtilitiesHOC(props: any) {
    const utilities = useUtilities();
    return <Component {...props} utilities={utilities} />;
  };
}
